/**
 * Utility functions for comparing and inspecting filter configurations.
 *
 * Kept separate from filterProducts (filtering logic) and buildInitialFilters
 * (config construction) so they can be tested independently.
 */

/**
 * Deep equality check for filter values.
 *
 * Handles:
 * - null / undefined
 * - primitive values (number, string, boolean)
 * - arrays — order-independent (sorted before compare)
 * - strict-match objects { selected: string[], strict: boolean }
 *
 * @param {*} a
 * @param {*} b
 * @returns {boolean}
 */
export function areFilterConfigsEqual(a, b) {
  if (a === b) return true
  if (a == null || b == null) return a === b
  if (typeof a !== typeof b) return false

  // Both arrays
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false
    const sa = [...a].map(String).sort()
    const sb = [...b].map(String).sort()
    return sa.every((v, i) => v === sb[i])
  }

  // Both strict-match objects { selected, strict }
  if (
    typeof a === 'object' && typeof b === 'object' &&
    !Array.isArray(a) && !Array.isArray(b) &&
    'selected' in a && 'selected' in b
  ) {
    if (a.strict !== b.strict) return false
    return areFilterConfigsEqual(a.selected, b.selected)
  }

  return false
}

/**
 * Returns true when the filter value differs from the CMS default.
 * Drives FilterCard isActive accent. Uses deep equality via areFilterConfigsEqual
 * so it works for all value shapes (arrays, strict-match objects, primitives).
 *
 * @param {string} id                    - filter id
 * @param {object} filtersConfig         - current active values
 * @param {object} defaultFiltersConfig  - CMS default state (overlay applied)
 * @returns {boolean}
 */
export function isFilterActive(id, filtersConfig, defaultFiltersConfig) {
  const value = filtersConfig?.[id]
  // Empty array = "show all" — same functional effect as default, not an active filter
  if (Array.isArray(value) && value.length === 0) return false
  return !areFilterConfigsEqual(value, defaultFiltersConfig?.[id])
}

/**
 * Counts filters that differ from their default values.
 * Used by UI to show "X active filters" badge.
 *
 * @param {Object}    filtersConfig         — current active filter state
 * @param {Object}    defaultFiltersConfig  — full-range defaults from buildInitialFilters
 * @param {Set<string>} [skipIds]           — filter ids to exclude from the count
 *                                            (e.g. UI mirrors like price_input)
 * @returns {number}
 */
export function countActiveFilters(filtersConfig, defaultFiltersConfig, skipIds = new Set()) {
  return Object.keys(filtersConfig).reduce((count, key) => {
    if (skipIds.has(key)) return count
    const active = filtersConfig[key]
    // Empty array = "show all" — treated as inactive regardless of default
    if (Array.isArray(active) && active.length === 0) return count
    return areFilterConfigsEqual(active, defaultFiltersConfig[key])
      ? count
      : count + 1
  }, 0)
}

/**
 * Applies a `Partial<FiltersConfig>` overlay on top of the full-range config.
 * Only keys present in the overlay are overwritten — the rest keeps range values.
 * Used to build defaultFiltersConfig from fullFiltersRange + panelSettings overlay.
 *
 * @param {Object} fullRangeConfig
 * @param {Object|null|undefined} overlay
 * @returns {Object}
 */
export function applyInitialFilteringOverlay(fullRangeConfig, overlay) {
  if (!overlay || typeof overlay !== 'object') return fullRangeConfig
  return { ...fullRangeConfig, ...overlay }
}

/**
 * Converts the full-range output of buildInitialFilters to an initial active config
 * where plain-array MultiSelect values start empty (empty = "show all").
 *
 * Only plain arrays for ids in `multiSelectIds` are converted to `[]`.
 * RangeSlider [min, max] arrays (rooms, area, price) and strict-match
 * { selected, strict } objects are left unchanged.
 *
 * @param {Object}      fullRangeConfig  — output of buildInitialFilters
 * @param {Set<string>} multiSelectIds   — set of filter ids with component MULTI_SELECT
 * @returns {Object}
 */
export function buildInitialActiveConfig(fullRangeConfig, multiSelectIds) {
  return Object.fromEntries(
    Object.entries(fullRangeConfig).map(([k, v]) => [
      k,
      multiSelectIds.has(k) && Array.isArray(v) ? [] : v,
    ])
  )
}

/**
 * Rebases the active filter state onto a newer computed default config.
 *
 * Preserves user-changed values while updating keys that were still equal to the
 * previous default or missing entirely.
 *
 * @param {Object|null|undefined} prevActiveConfig
 * @param {Object|null|undefined} prevDefaultConfig
 * @param {Object|null|undefined} nextDefaultConfig
 * @returns {Object|null|undefined}
 */
export function rebaseFiltersConfig(prevActiveConfig, prevDefaultConfig, nextDefaultConfig) {
  if (!nextDefaultConfig) return nextDefaultConfig
  if (!prevActiveConfig) return nextDefaultConfig

  const result = { ...prevActiveConfig }

  for (const [key, nextDefaultValue] of Object.entries(nextDefaultConfig)) {
    const prevActiveValue = prevActiveConfig[key]
    const prevDefaultValue = prevDefaultConfig?.[key]

    if (prevActiveValue === undefined || areFilterConfigsEqual(prevActiveValue, prevDefaultValue)) {
      result[key] = nextDefaultValue
    }
  }

  return result
}